import { create } from 'zustand';
import * as api from '../lib/api';
import { useBillingStore } from './billing';

const TOKEN_KEY = 'urblock_access_token';
const REFRESH_KEY = 'urblock_refresh_token';

export interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
}

export interface Tenant {
  id: string;
  company_name: string;
  plan: string;
  created: number;
  wallet_mode: 'connect';
  connect_account_address: string | null;
}

function mapUser(me: api.MeResponse): User {
  return {
    id: me.id,
    email: me.email,
    full_name: me.full_name,
    role: me.role,
  };
}

function mapTenant(tenant: api.MeResponse['tenant']): Tenant {
  return {
    id: tenant.id,
    company_name: tenant.company_name,
    plan: tenant.plan,
    created: tenant.created,
    wallet_mode: tenant.wallet_mode,
    connect_account_address: tenant.connect_account_address,
  };
}

async function hydrateAuthState(
  res: api.AuthResponse,
  set: (partial: Partial<AuthState>) => void,
): Promise<void> {
  localStorage.setItem(TOKEN_KEY, res.access_token);
  localStorage.setItem(REFRESH_KEY, res.refresh_token);

  set({
    token: res.access_token,
    refreshTokenValue: res.refresh_token,
    user: res.user ? { id: res.user.id, email: res.user.email, full_name: res.user.full_name, role: res.user.role } : null,
    tenant: res.tenant
      ? {
          id: res.tenant.id,
          company_name: res.tenant.company_name,
          plan: 'sandbox',
          created: res.tenant.created,
          wallet_mode: 'connect',
          connect_account_address: null,
        }
      : null,
    isAuthenticated: true,
  });

  try {
    const me = await api.getMe(res.access_token);
    set({
      user: mapUser(me),
      tenant: mapTenant(me.tenant),
    });
  } catch {
    /* non-critical */
  }

  void useBillingStore.getState().fetchBillingInfo(res.access_token);
}

interface AuthState {
  token: string | null;
  refreshTokenValue: string | null;
  user: User | null;
  tenant: Tenant | null;
  isLoading: boolean;
  isAuthenticated: boolean;

  /* actions */
  initialize: () => Promise<void>;
  unifiedAuth: (data:
    | { method: 'email'; email: string; password: string }
    | { method: 'siwe'; message: string; signature: string; address: string }
    | { method: 'oauth'; provider: 'google' | 'apple'; id_token: string }
    | { method: 'passkey'; flow: 'register'; challenge: string; credential_id: string; client_data_json: string; public_key: { x: string; y: string } }
    | { method: 'passkey'; flow: 'authenticate'; challenge: string; credential_id: string; client_data_json: string; authenticator_data: string; passkey_signature: string }
  ) => Promise<void>;
  register: (data: { email: string; password: string; company_name: string; full_name: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  token: null,
  refreshTokenValue: null,
  user: null,
  tenant: null,
  isLoading: true,
  isAuthenticated: false,

  initialize: async () => {
    /* Wire transparent 401 recovery: api client asks us to refresh, we hand back
       a fresh access token so the original request can be retried once. */
    api.setUnauthorizedHandler(async () => {
      const ok = await get().refresh();
      return ok ? get().token : null;
    });

    /* 1. Check URL hash for cross-subdomain callback */
    const hash = window.location.hash;
    if (hash.includes('access_token=')) {
      const params = new URLSearchParams(hash.substring(1));
      const accessToken = params.get('access_token');
      const refreshToken = params.get('refresh_token');
      if (accessToken) {
        localStorage.setItem(TOKEN_KEY, accessToken);
        if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }

    /* 2. Load from localStorage */
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshTokenValue = localStorage.getItem(REFRESH_KEY);

    if (!token) {
      set({ isLoading: false });
      return;
    }

    /* 3. Validate token by fetching /auth/me */
    try {
      const me = await api.getMe(token);
      set({
        token,
        refreshTokenValue,
        user: mapUser(me),
        tenant: mapTenant(me.tenant),
        isAuthenticated: true,
        isLoading: false,
      });
      void useBillingStore.getState().fetchBillingInfo(token);
    } catch {
      /* Token expired — try refresh */
      if (refreshTokenValue) {
        const ok = await get().refresh();
        if (!ok) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(REFRESH_KEY);
        }
      } else {
        localStorage.removeItem(TOKEN_KEY);
      }
      set({ isLoading: false });
    }
  },

  unifiedAuth: async (data) => {
    const res = await api.unifiedAuth(data);
    await hydrateAuthState(res, set);
  },

  register: async (data) => {
    const res = await api.register(data);
    await hydrateAuthState(res, set);
  },

  logout: async () => {
    const { token, refreshTokenValue } = get();
    try {
      if (token && refreshTokenValue) {
        await api.logout(refreshTokenValue, token);
      }
    } catch {
      /* best-effort */
    }
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    set({ token: null, refreshTokenValue: null, user: null, tenant: null, isAuthenticated: false });
    useBillingStore.getState().reset();
  },

  refresh: async () => {
    const refreshTokenValue = get().refreshTokenValue ?? localStorage.getItem(REFRESH_KEY);
    if (!refreshTokenValue) return false;

    try {
      const res = await api.refreshToken(refreshTokenValue);
      localStorage.setItem(TOKEN_KEY, res.access_token);
      localStorage.setItem(REFRESH_KEY, res.refresh_token);
      set({ token: res.access_token, refreshTokenValue: res.refresh_token, isAuthenticated: true });

      const me = await api.getMe(res.access_token);
      set({
        user: mapUser(me),
        tenant: mapTenant(me.tenant),
      });
      return true;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_KEY);
      set({ token: null, refreshTokenValue: null, user: null, tenant: null, isAuthenticated: false });
      return false;
    }
  },
}));
